import { isFunction, isNil } from 'lodash';
import { log } from '~frontend-common/services/logger';

export interface Clarity {
  // https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api#javascript-apis
  (
    action: 'identify',
    customId: string,
    customSessionId?: string,
    customPageId?: string,
    friendlyName?: string,
  ): Promise<void>;
  (action: 'consent', consent?: boolean): Promise<void>;
  (action: 'set', key: string, value: string): Promise<void>;
  (action: 'event', value: string): Promise<void>;
}

type Action = (clarity: Clarity) => void | Promise<void>;
const pendingActions: Action[] = [];

/**
 * TLDR: Because of the way that clarity works, we cannot keep a reference to it, so instead we keep track of if it is initialized.
 *
 * The initial Clarity script is literally only a queue, which keeps track of actions. This is done so that we are able to execute actions ASAP, while the script is
 * being loaded.
 * When the script is finally loaded, every queued actions gets processed, and then the `window.clarity` gets replaced by the actual Clarity
 * script, so if we keep a reference to it, we will reference the original queue, which has no functionality expect pushing into a queue.
 * Once the actual script in loaded, the queue no longer gets processed.
 */
let clarityInitialization: Promise<boolean>;

export function enqueueClarityAction(action: Action): void {
  pendingActions.push(action);
  if (pendingActions.length > 1) {
    // If other pending actions, it means that a worker is already processing them
    return;
  }

  if (isNil(clarityInitialization)) {
    clarityInitialization = detectClarity();
  }

  void clarityInitialization.then(async (isClarityInitialized) => {
    if (!isClarityInitialized) {
      // If clarity could not be initialized, clear queue and do nothing.
      pendingActions.splice(0, pendingActions.length);
      return;
    }

    while (pendingActions.length > 0) {
      // Keep action in array until completed
      const action = pendingActions[0];
      try {
        await action(window['clarity']);
      } catch (e) {
        log.error(`Failed to perform Clarity action: ${e}`);
      }
      pendingActions.shift();
    }
  });
}

async function detectClarity(): Promise<boolean> {
  // Try for 10s with 500ms internal
  const INTERVAL = 500;
  const MAX_ATTEMPTS = 10_000 / INTERVAL;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, INTERVAL));
    }

    const clarity = window['clarity'];
    if (isFunction(clarity)) {
      return true;
    }
  }

  log.warn(`Clarity API could not be found after ${MAX_ATTEMPTS} attempts.`);
  return false;
}
